import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import BudgetItemModal from './BudgetItemModal';
import BudgetAdjustmentModal from './BudgetAdjustmentModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';

interface BudgetTabProps {
  tournamentId: string;
  onRefresh: () => void;
}

interface BudgetItem {
  id: string;
  category: string;
  subcategory: string | null;
  description: string;
  allocated_amount: number;
  spent_amount: number;
  fiscal_year: number | null;
  status: string;
}

interface BudgetAdjustment {
  id: string;
  budget_item_id: string;
  previous_amount: number;
  new_amount: number;
  adjustment_date: string;
  reason: string;
}

export default function BudgetTab({ tournamentId, onRefresh }: BudgetTabProps) {
  const [budgetItems, setBudgetItems] = useState<BudgetItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<BudgetItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BudgetItem | null>(null);
  const [totalBudget, setTotalBudget] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [adjustmentHistory, setAdjustmentHistory] = useState<BudgetAdjustment[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tournamentId) {
      fetchBudgetItems();
    }
  }, [tournamentId]);

  useEffect(() => {
    filterBudgetItems();
  }, [budgetItems, searchTerm, selectedCategory, selectedStatus]);

  const fetchBudgetItems = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch budget items from Supabase
      const { data, error: fetchError } = await supabase
        .from('budget_items')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('category', { ascending: true });
      
      if (fetchError) throw fetchError;
      
      if (data) {
        setBudgetItems(data);
        
        // Calculate totals
        const allocated = data.reduce((sum, item) => sum + (item.allocated_amount || 0), 0);
        const spent = data.reduce((sum, item) => sum + (item.spent_amount || 0), 0);
        
        setTotalBudget(allocated);
        setTotalSpent(spent);
      }
    } catch (err) {
      console.error('Error fetching budget items:', err);
      setError('Failed to load budget data');
      toast.error('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdjustmentHistory = async (budgetItemId: string) => {
    try {
      const { data, error } = await supabase
        .from('budget_adjustments')
        .select('*')
        .eq('budget_item_id', budgetItemId)
        .order('adjustment_date', { ascending: false });
      
      if (error) throw error;
      
      setAdjustmentHistory(data || []);
      
    } catch (err) {
      console.error('Error fetching adjustment history:', err);
      toast.error('Failed to load adjustment history');
    }
  };

  const filterBudgetItems = () => {
    let filtered = [...budgetItems];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        item => 
          item.description.toLowerCase().includes(term) ||
          item.category.toLowerCase().includes(term) ||
          (item.subcategory && item.subcategory.toLowerCase().includes(term))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }
    
    setFilteredItems(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredItems.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredItems.length / recordsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleAddBudgetItem = () => {
    setSelectedItem(null);
    setIsItemModalOpen(true);
  };

  const handleEditBudgetItem = (item: BudgetItem) => {
    setSelectedItem(item);
    setIsItemModalOpen(true);
  };

  const handleAdjustBudget = (item: BudgetItem) => {
    setSelectedItem(item);
    fetchAdjustmentHistory(item.id);
    setIsAdjustmentModalOpen(true);
  };

  const handleDeleteBudgetItem = (item: BudgetItem) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;
    
    try {
      const { error } = await supabase
        .from('budget_items')
        .delete()
        .eq('id', selectedItem.id);
      
      if (error) throw error;
      
      await fetchBudgetItems();
      onRefresh();
      toast.success('Budget item deleted successfully');
    } catch (err) {
      console.error('Error deleting budget item:', err);
      toast.error('Failed to delete budget item');
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'venue':
        return 'bg-blue-100 text-blue-800';
      case 'prize':
        return 'bg-green-100 text-green-800';
      case 'marketing':
        return 'bg-purple-100 text-purple-800';
      case 'operations':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUtilizationColor = (percentage: number) => {
    if (percentage > 100) return 'text-red-600';
    if (percentage > 90) return 'text-yellow-600';
    if (percentage > 70) return 'text-blue-600';
    return 'text-green-600';
  };

  const getUtilizationIcon = (percentage: number) => {
    if (percentage > 100) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
    }
    if (percentage > 90) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
    }
    if (percentage > 70) {
      return <CheckIcon className="w-4 h-4 text-blue-600" />;
    }
    return <CheckIcon className="w-4 h-4 text-green-600" />;
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <div className="p-6">
      {/* Summary Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-golf-gray">Budget Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-500">Total Budget</div>
                <div className="text-xl font-semibold text-golf-gray">{formatCurrency(totalBudget)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-500">Total Spent</div>
                <div className="text-xl font-semibold text-golf-gray">{formatCurrency(totalSpent)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-500">Remaining</div>
                <div className="text-xl font-semibold text-golf-gray">{formatCurrency(totalBudget - totalSpent)}</div>
              </div>
            </div>
          </div>
          <button
            onClick={handleAddBudgetItem}
            className="modern-button px-4 py-2 text-white font-medium inline-flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Budget Item</span>
          </button>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
          <div
            className="bg-red-500 rounded-full h-4"
            style={{
              width: `${totalBudget > 0 ? Math.min((totalSpent / totalBudget) * 100, 100) : 0}%`
            }}
          />
        </div>
        
        <div className="flex justify-between text-sm text-gray-500">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search budget items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              />
            </div>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Categories</option>
                <option value="venue">Venue & Facilities</option>
                <option value="prize">Prize Money</option>
                <option value="marketing">Marketing & PR</option>
                <option value="operations">Staff & Operations</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </motion.div>
        )}
      </div>

      {/* Budget Items Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-golf-gray">Budget Items</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">
            <p>{error}</p>
            <button
              onClick={fetchBudgetItems}
              className="mt-4 text-golf-orange hover:text-golf-green"
            >
              Try Again
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No budget items found
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Allocated</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilization</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentRecords.map((item) => {
                    const utilizationPercentage = item.allocated_amount > 0 
                      ? (item.spent_amount / item.allocated_amount) * 100 
                      : 0;
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{item.description}</div>
                          {item.subcategory && (
                            <div className="text-sm text-gray-500">{item.subcategory}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(item.category)}`}>
                            {item.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(item.allocated_amount)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(item.spent_amount)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(item.allocated_amount - item.spent_amount)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getUtilizationIcon(utilizationPercentage)}
                            <span className={`ml-1 text-sm font-medium ${getUtilizationColor(utilizationPercentage)}`}>
                              {utilizationPercentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                            <div
                              className={`rounded-full h-1.5 ${
                                utilizationPercentage > 100
                                  ? 'bg-red-500'
                                  : utilizationPercentage > 90
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(utilizationPercentage, 100)}%` }}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleAdjustBudget(item)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Adjust
                            </button>
                            <button
                              onClick={() => handleEditBudgetItem(item)}
                              className="text-golf-orange hover:text-golf-green"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteBudgetItem(item)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredItems.length)} of {filteredItems.length} entries
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                
                {getPageNumbers().map((number) => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`px-3 py-1 rounded-lg ${
                      currentPage === number
                        ? 'bg-golf-orange text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {number}
                  </button>
                ))}
                
                {totalPages > 5 && (
                  <span className="text-gray-500">...</span>
                )}
                
                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages || totalPages === 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Budget Item Modal */}
      <BudgetItemModal
        isOpen={isItemModalOpen}
        onClose={() => {
          setIsItemModalOpen(false);
          setSelectedItem(null);
        }}
        budgetItem={selectedItem}
        tournamentId={tournamentId}
        onSuccess={() => {
          fetchBudgetItems();
          onRefresh();
        }}
      />

      {/* Budget Adjustment Modal */}
      <BudgetAdjustmentModal
        isOpen={isAdjustmentModalOpen}
        onClose={() => {
          setIsAdjustmentModalOpen(false);
          setSelectedItem(null);
        }}
        budgetItem={selectedItem}
        adjustmentHistory={adjustmentHistory}
        onSuccess={() => {
          fetchBudgetItems();
          onRefresh();
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedItem(null);
        }}
        onConfirm={confirmDelete}
        title={selectedItem?.description || ''}
      />
    </div>
  );
}